import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { History, Loader2, GraduationCap, Receipt, Wallet } from 'lucide-react';
import { formatDateTime } from '../../lib/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PreviousYears = () => {
    const [archives, setArchives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeYear, setActiveYear] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API_URL}/api/year-archives/parent/me`, { withCredentials: true });
                setArchives(r.data || []);
                if (r.data && r.data.length) setActiveYear(r.data[0].year);
            } catch (err) {
                if (err.response?.status === 403) toast.info(err.response.data?.detail || 'Previous years are not enabled by the school');
                else toast.error('Failed to load previous years');
            } finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="parent-previous-years-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2"><History className="h-6 w-6 text-[#7C3AED]" />Previous Years</h1>
                    <p className="text-[#4B5563]">A history of your children&apos;s registrations, fees and receipts from past school years.</p>
                </div>

                {archives.length === 0 ? (
                    <Card className="card-surface"><CardContent className="flex flex-col items-center justify-center py-16">
                        <History className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                        <p className="text-[#4B5563] text-lg">No archived years yet</p>
                        <p className="text-xs text-[#6B7280] mt-1">Once the school completes a year transfer, past-year data appears here.</p>
                    </CardContent></Card>
                ) : (
                    <Tabs value={activeYear} onValueChange={setActiveYear} className="space-y-4">
                        <TabsList>
                            {archives.map(a => (
                                <TabsTrigger key={a.year} value={a.year} data-testid={`year-tab-${a.year}`}>{a.year}</TabsTrigger>
                            ))}
                        </TabsList>
                        {archives.map(a => (
                            <TabsContent key={a.year} value={a.year}>
                                <YearArchiveView archive={a} />
                            </TabsContent>
                        ))}
                    </Tabs>
                )}
            </div>
        </DashboardLayout>
    );
};

const YearArchiveView = ({ archive }) => {
    const t = archive.totals || {};
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <Stat label="Total Fees" value={`€${(t.fees || 0).toFixed(2)}`} color="text-[#1F2937]" />
                <Stat label="Previous Debt" value={`€${(t.previous_debt || 0).toFixed(2)}`} color="text-[#EF4444]" />
                <Stat label="Discount" value={`−€${(t.discount || 0).toFixed(2)}`} color="text-[#7C3AED]" />
                <Stat label="Total Paid" value={`€${(t.paid || 0).toFixed(2)}`} color="text-[#10B981]" />
                <Stat label="Remaining" value={`€${(t.remaining || 0).toFixed(2)}`} color={t.remaining > 0 ? "text-[#F59E0B]" : "text-[#10B981]"} />
            </div>

            <Card className="card-surface">
                <CardHeader><CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><GraduationCap className="h-4 w-4 text-[#064E3B]" />Registered Students ({(archive.students || []).length})</CardTitle></CardHeader>
                <CardContent className="p-0">
                    {(archive.students || []).length ? (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader><TableRow>
                                    <TableHead>Reg #</TableHead>
                                    <TableHead>Student</TableHead>
                                    <TableHead>DOB</TableHead>
                                    <TableHead>Program</TableHead>
                                    <TableHead>Class</TableHead>
                                    <TableHead className="text-right">Fee</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {archive.students.map(s => (
                                        <TableRow key={s.id}>
                                            <TableCell className="font-mono text-xs">{s.reg_code}</TableCell>
                                            <TableCell>
                                                <div className="font-medium">{s.first_name} {s.last_name}</div>
                                                {(s.first_name_ar || s.last_name_ar) && <div className="text-xs text-[#4B5563]" dir="rtl">{s.first_name_ar} {s.last_name_ar}</div>}
                                            </TableCell>
                                            <TableCell className="tabular-nums text-sm">{s.date_of_birth}</TableCell>
                                            <TableCell><Badge variant="outline">{s.program_name || '—'}</Badge></TableCell>
                                            <TableCell className="text-sm">{s.class_name || '—'}</TableCell>
                                            <TableCell className="text-right tabular-nums">€{(s.annual_fee || 0).toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : <div className="p-6 text-center text-[#6B7280] text-sm">No student records</div>}
                </CardContent>
            </Card>

            <Card className="card-surface">
                <CardHeader><CardTitle className="text-base font-['Outfit'] flex items-center gap-2"><Receipt className="h-4 w-4 text-[#064E3B]" />Payment Receipts ({(archive.receipts || []).length})</CardTitle></CardHeader>
                <CardContent className="p-0">
                    {(archive.receipts || []).length ? (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader><TableRow>
                                    <TableHead>Receipt #</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Method</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {archive.receipts.map(r => (
                                        <TableRow key={r.id || r.receipt_number}>
                                            <TableCell className="font-mono text-xs">{r.receipt_number}</TableCell>
                                            <TableCell className="tabular-nums text-sm">{formatDateTime(r.created_at)}</TableCell>
                                            <TableCell className="capitalize">{r.payment_type}</TableCell>
                                            <TableCell className="capitalize text-sm">{(r.method || '').replace('_', ' ')}</TableCell>
                                            <TableCell className="text-right tabular-nums">€{(r.amount || 0).toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    ) : <div className="p-6 text-center text-[#6B7280] text-sm">No receipts recorded</div>}
                </CardContent>
            </Card>
        </div>
    );
};

const Stat = ({ label, value, color }) => (
    <Card className="card-surface"><CardContent className="p-4">
        <p className="text-xs text-[#6B7280]">{label}</p>
        <p className={`text-xl font-bold tabular-nums font-['Outfit'] ${color}`}>{value}</p>
    </CardContent></Card>
);

export default PreviousYears;
